import {
  auditEntries,
  countRawKeysWithPrefix,
  removeRawKeysWithPrefix,
} from "src/utils/storage";

// With bootstrap "localStorage" the SDK keeps a snapshot of every flag per context under this prefix and never evicts them.
export const launchDarklyCachePrefix = (envId: string) => `ld:${envId}:`;

// Mirrors the SDK's PersistentFlagStore key: base64 of the UTF-8 encoded JSON.
export const launchDarklyCacheKey = (envId: string, context: unknown) => {
  const bytes = new TextEncoder().encode(JSON.stringify(context));
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return `${launchDarklyCachePrefix(envId)}${btoa(binary)}`;
};

// Removes this environment's snapshots for every context except the current one, and returns how many it removed.
export const pruneLaunchDarklyCache = (
  envId: string,
  currentContext: unknown,
): number => {
  // The SDK's own bookkeeping, not a flag snapshot.
  const keep = new Set([`${launchDarklyCachePrefix(envId)}$diagnostics`]);
  if (currentContext) {
    keep.add(launchDarklyCacheKey(envId, currentContext));
  }
  return removeRawKeysWithPrefix(launchDarklyCachePrefix(envId), keep);
};

// Half of Chrome's 10 MiB localStorage quota, in the UTF-16 bytes auditEntries counts.
export const LAUNCHDARKLY_PRUNE_THRESHOLD_BYTES = 5 * 1024 * 1024;

// Snapshots run 80-170 KB, so fewer than this many can't be what is filling storage.
export const LAUNCHDARKLY_PRUNE_MIN_SNAPSHOTS = 10;

const localStorageBytes = () =>
  auditEntries().reduce((total, entry) => total + entry.bytes, 0);

// A pruned workspace's next load starts with no cached flags, so this only prunes when storage is near the quota.
export const pruneLaunchDarklyCacheIfNearQuota = (
  envId: string,
  currentContext: unknown,
  measureBytes: () => number = localStorageBytes,
): number => {
  // Counting keys reads no values, which keeps the common case cheap.
  if (
    countRawKeysWithPrefix(launchDarklyCachePrefix(envId)) <
    LAUNCHDARKLY_PRUNE_MIN_SNAPSHOTS
  ) {
    return 0;
  }
  if (measureBytes() <= LAUNCHDARKLY_PRUNE_THRESHOLD_BYTES) {
    return 0;
  }
  return pruneLaunchDarklyCache(envId, currentContext);
};
